import { Button } from "./ui/button";

/**
 * EmptyState - Reusable component for empty states across Stelx Pay
 * 
 * Design principles:
 * - Calm, supportive tone
 * - Never implies user failure
 * - Clear path forward with primary action
 */
const EmptyState = ({ 
  icon: Icon, 
  headline, 
  description, 
  actionLabel, 
  onAction,
  secondaryLabel,
  onSecondary,
  variant = "default" // "default" | "compact" | "inline"
}) => {
  if (variant === "compact") {
    return (
      <div className="glass rounded-2xl p-6 text-center" data-testid="empty-state-compact">
        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-3">
          <Icon className="w-6 h-6 text-primary/70" />
        </div>
        <h3 className="font-medium text-sm mb-1">{headline}</h3>
        <p className="text-xs text-muted-foreground mb-4">{description}</p>
        {actionLabel && onAction && (
          <Button 
            size="sm" 
            className="btn-primary"
            onClick={onAction}
            data-testid="empty-state-action"
          >
            {actionLabel}
          </Button>
        )}
      </div>
    );
  }

  if (variant === "inline") {
    return (
      <div className="flex items-center gap-4 p-4 glass rounded-xl" data-testid="empty-state-inline">
        <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 text-primary/70" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium">{headline}</p>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {actionLabel && onAction && (
          <Button 
            size="sm" 
            variant="outline"
            className="btn-secondary flex-shrink-0"
            onClick={onAction}
          >
            {actionLabel}
          </Button>
        )}
      </div>
    );
  }

  // Default variant
  return (
    <div className="text-center py-12 px-6" data-testid="empty-state">
      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-4">
        <Icon className="w-8 h-8 text-primary/70" />
      </div>
      <h2 className="font-heading font-medium text-lg mb-2">{headline}</h2>
      <p className="text-sm text-muted-foreground mb-6 max-w-xs mx-auto">
        {description}
      </p>
      <div className="flex flex-col gap-2 items-center">
        {actionLabel && onAction && (
          <Button 
            className="btn-primary"
            onClick={onAction}
            data-testid="empty-state-action"
          >
            {actionLabel}
          </Button>
        )}
        {secondaryLabel && onSecondary && (
          <Button 
            variant="ghost"
            size="sm"
            className="text-muted-foreground"
            onClick={onSecondary}
          >
            {secondaryLabel}
          </Button>
        )}
      </div>
    </div>
  );
};

/**
 * TrustBadge - Microcopy for trust and transparency
 */
export const TrustBadge = ({ 
  icon: Icon, 
  text,
  variant = "default" // "default" | "security" | "info" | "ai"
}) => {
  const variants = {
    default: "text-muted-foreground/60",
    security: "text-emerald-400/70",
    info: "text-blue-400/70",
    ai: "text-primary/70"
  };

  return (
    <div className={`flex items-start gap-2 text-[11px] ${variants[variant]}`} data-testid="trust-badge">
      {Icon && <Icon className="w-3 h-3 flex-shrink-0 mt-0.5" />}
      <span>{text}</span>
    </div>
  );
};

/**
 * SecurityNote - For sensitive actions
 */
export const SecurityNote = ({ text }) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-500/5 border border-emerald-500/10" data-testid="security-note">
    <div className="w-4 h-4 rounded-full bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
      <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
    </div>
    <span className="text-[11px] text-emerald-400/80">{text}</span>
  </div>
);

/**
 * ConfirmationNote - For payment/action confirmations
 */
export const ConfirmationNote = ({ text }) => (
  <p className="text-[11px] text-muted-foreground/60 text-center mt-4" data-testid="confirmation-note">
    {text}
  </p>
);

export default EmptyState;
